import { useEffect, useState } from "react";
import { Phone, Mail, Instagram, Facebook, MapPin } from "lucide-react";
import { BRAND } from "@/constants/branding";
import api from "@/lib/api";

export const CONTENT = {
  about: {
    title: "About Zilkart",
    body: [
      "Zilkart is a modern multi-vendor e-commerce marketplace built for value — connecting customers with trusted vendors across electronics, fashion, footwear, home & living, beauty and more.",
      "Our mission is simple: make quality products accessible to everyone at fair prices, backed by reliable delivery and friendly support. Every vendor on Zilkart is onboarded to ensure a safe and seamless shopping experience.",
      "Whether you're shopping for the latest gadgets or everyday essentials, Zilkart brings it all together in one smooth, dependable platform.",
    ],
  },
  help: {
    title: "Help & Support",
    body: [
      "Need a hand? Our support team is here to help you with orders, deliveries, returns and account questions.",
      "Browse your orders from your account to track status. For anything else, reach out using the contact details below and we'll get back to you promptly.",
    ],
  },
  privacy: {
    title: "Privacy Policy",
    body: [
      "Your privacy matters to us. Zilkart collects only the information necessary to process your orders and improve your shopping experience — such as your name, contact details and delivery address.",
      "We never sell your personal data. Information is shared with vendors and delivery partners strictly to fulfil your orders. Payment information is handled securely and is not stored on our servers.",
      "You may request access to or deletion of your data at any time by contacting our support team.",
    ],
  },
  terms: {
    title: "Terms & Conditions",
    body: [
      "By using Zilkart you agree to shop and transact in good faith. Product listings, prices and availability are provided by vendors and may change without notice.",
      "Orders are subject to acceptance and availability. Zilkart acts as a marketplace connecting buyers and sellers, and reserves the right to cancel orders in cases of pricing errors, fraud or stock issues.",
      "Continued use of the platform constitutes acceptance of these terms.",
    ],
  },
  shipping: {
    title: "Shipping Policy",
    body: [
      "Zilkart partners with reliable logistics providers to deliver your orders safely and on time. Delivery timelines vary by location and product.",
      "You can track the status of every order from your account. Standard delivery is currently free on all orders.",
      "In rare cases of delays due to weather or operational issues, our team will keep you informed.",
    ],
  },
  refund: {
    title: "Refund & Return Policy",
    body: [
      "We want you to love what you buy. If a product arrives damaged, defective or not as described, you may request a return within the eligible return window.",
      "Once your return is received and inspected, approved refunds are processed to your original payment method or as store credit, depending on the payment type.",
      "Certain items may be non-returnable for hygiene or safety reasons — these are clearly marked where applicable.",
    ],
  },
};

export default function StaticPage({ slug }) {
  if (slug === "contact") return <ContactPage />;
  return <ContentPage slug={slug} />;
}

// Text can be edited from the admin CMS; the built-in copy above is the fallback.
function ContentPage({ slug }) {
  const [override, setOverride] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let live = true;
    setLoaded(false); setOverride(null);
    api.get(`/cms/pages/${slug}`).then((r) => live && setOverride(r.data)).catch(() => {}).finally(() => live && setLoaded(true));
    return () => { live = false; };
  }, [slug]);
  const page = override || CONTENT[slug];
  if (!loaded || !page) return null;
  return (
    <div className="shell max-w-3xl py-6 sm:py-10 animate-fade-up" data-testid={`static-page-${slug}`}>
      <div className="surface p-6 sm:p-10">
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-deep mb-6">{page.title}</h1>
      <div className="max-w-none space-y-4">
        {page.body.map((p, i) => (
          <p key={i} className="text-muted-foreground leading-relaxed">{p}</p>
        ))}
      </div>
      </div>
    </div>
  );
}

function ContactPage() {
  return (
    <div className="shell max-w-5xl py-6 sm:py-10 animate-fade-up" data-testid="static-page-contact">
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-deep mb-2">Contact Us</h1>
      <p className="text-muted-foreground mb-8">We'd love to hear from you. Reach out through any of the channels below.</p>
      <div className="grid sm:grid-cols-2 gap-5">
        <a href={`tel:${BRAND.phone}`} className="flex items-center gap-4 p-6 surface hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(6,48,28,0.35)] transition-all" data-testid="contact-phone">
          <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Phone className="h-6 w-6" /></div>
          <div><p className="text-sm text-muted-foreground">Call us</p><p className="font-semibold">{BRAND.phone}</p></div>
        </a>
        <a href={`mailto:${BRAND.email}`} className="flex items-center gap-4 p-6 surface hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(6,48,28,0.35)] transition-all" data-testid="contact-email">
          <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Mail className="h-6 w-6" /></div>
          <div className="min-w-0"><p className="text-sm text-muted-foreground">Email us</p><p className="font-semibold break-all">{BRAND.email}</p></div>
        </a>
        <a href={BRAND.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-6 surface hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(6,48,28,0.35)] transition-all" data-testid="contact-instagram">
          <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Instagram className="h-6 w-6" /></div>
          <div><p className="text-sm text-muted-foreground">Follow us</p><p className="font-semibold">Instagram</p></div>
        </a>
        <a href={BRAND.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-6 surface hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(6,48,28,0.35)] transition-all" data-testid="contact-facebook">
          <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Facebook className="h-6 w-6" /></div>
          <div><p className="text-sm text-muted-foreground">Like us</p><p className="font-semibold">Facebook</p></div>
        </a>
      </div>
      <div className="mt-6 flex items-center gap-2 text-muted-foreground text-sm">
        <MapPin className="h-4 w-4" /> Serving customers across India
      </div>
    </div>
  );
}
